import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ArrowsLeftRight, Plus, CaretDown } from "@phosphor-icons/react";
import { formatDate } from "@/lib/dateFmt";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * v1.29.22 — Move a single ledger row to another party.
 * v1.29.23 — Also accepts an `entries` array for bulk-move: PATCHes each
 * id in turn against the same destination party, then reports a summary
 * toast. `entry` (singular) still supported for backward compatibility.
 *
 * Uses the existing PATCH /transactions/{id} endpoint (which already handles
 * commission / portal-fee rebuild + audit-log append when party_id changes).
 *
 * Props:
 *   entry    — single ledger row (legacy path).
 *   entries  — array of ledger rows for bulk mode.
 *   parties, currentPartyId, onDone, onClose — as before.
 */
export default function MoveEntryDialog({ entry, entries, parties, currentPartyId, onDone, onClose }) {
    const rows = useMemo(() => {
        if (Array.isArray(entries) && entries.length) return entries;
        if (entry) return [entry];
        return [];
    }, [entry, entries]);
    const isBulk = rows.length > 1;
    const totals = useMemo(() => {
        let d = 0, c = 0;
        for (const r of rows) {
            d += Number(r.paid || 0);
            c += Number(r.recd || 0);
        }
        return { count: rows.length, debit: d, credit: c };
    }, [rows]);
    const [open, setOpen] = useState(true);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [targetId, setTargetId] = useState("");
    const [busy, setBusy] = useState(false);
    const [creatingParty, setCreatingParty] = useState(false);
    const [newPartyName, setNewPartyName] = useState("");
    const [newPartyBusy, setNewPartyBusy] = useState(false);
    const inputRef = useRef(null);

    // Filter out the source party — you can't move to yourself.
    const options = useMemo(() => {
        const active = (parties || []).filter((p) => p.active !== false && p.id !== currentPartyId);
        if (!query.trim()) return active;
        const q = query.trim().toLowerCase();
        return active.filter((p) => (p.name || "").toLowerCase().includes(q));
    }, [parties, currentPartyId, query]);

    const targetParty = useMemo(() => (parties || []).find((p) => p.id === targetId) || null, [parties, targetId]);

    // Whether the typed query looks like a brand-new party (no exact match).
    const canCreate = useMemo(() => {
        const q = query.trim();
        if (q.length < 2) return false;
        const exists = (parties || []).some((p) => (p.name || "").trim().toLowerCase() === q.toLowerCase());
        return !exists;
    }, [query, parties]);

    useEffect(() => {
        if (pickerOpen) requestAnimationFrame(() => inputRef.current?.focus?.());
    }, [pickerOpen]);

    const close = () => { setOpen(false); onClose?.(); };

    const submit = async () => {
        if (!targetId) { toast.error("Pick a destination party first"); return; }
        if (!rows.length) { toast.error("Nothing to move"); return; }
        setBusy(true);
        try {
            // v1.29.23 — Move each row via PATCH /transactions/{id}. Serial
            // (not Promise.all) so a single bad row doesn't leave the others
            // in an ambiguous state; failure of one still reports what did
            // move. Backend already handles commission rebuild + audit log
            // append per row.
            let ok = 0;
            const failures = [];
            for (const r of rows) {
                try {
                    await api.patch(`/transactions/${r.id}`, { party_id: targetId });
                    ok += 1;
                } catch (e) {
                    failures.push({ id: r.id, err: formatApiError(e) });
                }
            }
            const partyName = targetParty?.name || "party";
            if (ok > 0 && failures.length === 0) {
                toast.success(rows.length === 1 ? `Entry moved to ${partyName}` : `${ok} entries moved to ${partyName}`);
            } else if (ok > 0 && failures.length > 0) {
                toast.warning(`Moved ${ok} of ${rows.length}. ${failures.length} failed — see console.`);
                // eslint-disable-next-line no-console
                console.warn("[Move Entry] partial failure:", failures);
            } else {
                toast.error(failures[0]?.err || "Move failed");
            }
            if (ok > 0) onDone?.(targetParty);
            if (failures.length === 0) setOpen(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    };

    const createParty = async () => {
        const name = (query || newPartyName).trim();
        if (name.length < 2) { toast.error("Enter a name of at least 2 characters"); return; }
        setNewPartyBusy(true);
        try {
            const r = await api.post("/parties", { name });
            const created = r.data;
            toast.success(`Party "${created.name}" created`);
            // Push into parent's parties list on success via onDone-like signal — the
            // parent already refreshes parties on ledger reload, but for immediate
            // continuity we set targetId directly on the just-created party.
            setTargetId(created.id);
            setPickerOpen(false);
            setCreatingParty(false);
            setNewPartyName("");
            setQuery("");
            // The parent's parties list may be stale for one render; that's OK — the
            // targetParty label falls back to "party" until the parent refetches.
            window.dispatchEvent(new CustomEvent("micm-party-created", { detail: created }));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setNewPartyBusy(false); }
    };

    if (!rows.length) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
            <DialogContent data-testid="move-entry-dialog" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ArrowsLeftRight size={18} weight="duotone" className="text-blue-600" />
                        {isBulk ? `Move ${totals.count} Entries to Another Party` : "Move Entry to Another Party"}
                    </DialogTitle>
                </DialogHeader>

                {/* v1.29.23 — Read-only summary. Single entry shows date/type/amount/ref;
                    bulk shows count + total debit + total credit so the user can sanity-
                    check before hitting Move. */}
                {isBulk ? (
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm" data-testid="move-entry-bulk-summary">
                        <div className="text-[10px] uppercase tracking-widest text-blue-800">Moving {totals.count} entries</div>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Debit</div>
                                <div className="font-semibold tabular-nums text-rose-700">₹{fmt(totals.debit)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Total Credit</div>
                                <div className="font-semibold tabular-nums text-emerald-700">₹{fmt(totals.credit)}</div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="move-entry-summary">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Date</div>
                                <div className="text-slate-800">{formatDate(rows[0].date)}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Type</div>
                                <div className="text-slate-800">{rows[0].remark || rows[0]._type || "—"}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Amount</div>
                                <div className="font-semibold tabular-nums text-slate-900">
                                    ₹{fmt(rows[0].recd || rows[0].paid || rows[0].amount || 0)}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-slate-500">Reference</div>
                                <div className="font-mono text-xs text-slate-700 truncate">{rows[0].utr || rows[0].reference || "—"}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Move-to picker */}
                <div className="mt-2 space-y-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-600">Move To Party</Label>
                    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                data-testid="move-entry-party-picker"
                                className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:bg-slate-50"
                            >
                                <span className={targetParty ? "text-slate-800" : "text-slate-400"}>
                                    {targetParty ? targetParty.name : "Search parties…"}
                                </span>
                                <CaretDown size={12} className="text-slate-400" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    ref={inputRef}
                                    value={query}
                                    onValueChange={setQuery}
                                    placeholder="Type to search parties…"
                                    data-testid="move-entry-party-search"
                                />
                                <CommandList>
                                    <CommandEmpty>
                                        {canCreate ? (
                                            <button
                                                type="button"
                                                data-testid="move-entry-create-party-empty"
                                                onClick={() => setCreatingParty(true)}
                                                className="w-full px-3 py-2 text-left text-sm text-blue-700 hover:bg-blue-50"
                                            >
                                                <Plus size={12} className="mr-1 inline" />
                                                Create new party: <b>"{query.trim()}"</b>
                                            </button>
                                        ) : (
                                            <span className="block px-3 py-2 text-sm text-slate-400">No matching party.</span>
                                        )}
                                    </CommandEmpty>
                                    <CommandGroup>
                                        {options.map((p) => (
                                            <CommandItem
                                                key={p.id}
                                                value={p.id}
                                                onSelect={() => { setTargetId(p.id); setPickerOpen(false); setQuery(""); }}
                                                data-testid={`move-entry-party-opt-${p.id}`}
                                            >
                                                {p.name}
                                            </CommandItem>
                                        ))}
                                        {canCreate && options.length > 0 && (
                                            <CommandItem
                                                value={`__create__${query}`}
                                                onSelect={() => setCreatingParty(true)}
                                                data-testid="move-entry-create-party"
                                                className="border-t border-slate-100 text-blue-700"
                                            >
                                                <Plus size={12} className="mr-2 inline" />
                                                Create new party: <b className="ml-1">"{query.trim()}"</b>
                                            </CommandItem>
                                        )}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>

                {/* Inline "New party" sub-form */}
                {creatingParty && (
                    <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-3" data-testid="move-entry-new-party-form">
                        <div className="text-xs font-semibold uppercase tracking-widest text-blue-800">Create new party</div>
                        <div className="mt-2 flex items-center gap-2">
                            <Input
                                data-testid="move-entry-new-party-name"
                                autoFocus
                                value={newPartyName || query}
                                onChange={(e) => setNewPartyName(e.target.value)}
                                placeholder="Party name"
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createParty(); } }}
                                className="flex-1"
                            />
                            <Button
                                type="button"
                                data-testid="move-entry-new-party-save"
                                onClick={createParty}
                                disabled={newPartyBusy}
                                size="sm"
                                className="bg-blue-600 text-white hover:bg-blue-700"
                            >
                                {newPartyBusy ? "Creating…" : "Create & Select"}
                            </Button>
                            <button
                                type="button"
                                onClick={() => { setCreatingParty(false); setNewPartyName(""); }}
                                className="text-xs text-slate-500 underline"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                <DialogFooter className="mt-2">
                    <Button type="button" variant="ghost" onClick={close} data-testid="move-entry-cancel">Cancel</Button>
                    <Button
                        type="button"
                        data-testid="move-entry-confirm"
                        onClick={submit}
                        disabled={!targetId || busy}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                        {busy ? "Moving…" : "Move Entry"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
